/**
 * Elegant click sound — synthesized in-browser with the Web Audio API.
 *
 * Why synthesised? No external file fetches, no licensing, total control over
 * timbre. The result is a soft, slightly-reverberated low-frequency "tap"
 * reminiscent of a felt mallet on a wooden block — discreet, refined.
 *
 * Usage:
 *   import { playElegantClick } from "@/lib/sound";
 *   onClick={() => { playElegantClick(); ... }}
 *
 * Settings persisted in localStorage.pk2_sound_v1 ({enabled: bool, volume: 0..1}).
 * Browsers block AudioContext until a user gesture, so the first call to
 * playElegantClick() inside an onClick will succeed (we lazy-init the context).
 */

const LS_KEY = "pk2_sound_v1";

let _ctx = null;
let _reverb = null;
let _master = null;

function getSettings() {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return {
      enabled: s.enabled !== false, // default ON
      volume: typeof s.volume === "number" ? s.volume : 0.45,
    };
  } catch {
    return { enabled: true, volume: 0.45 };
  }
}

export function setSoundSettings(patch) {
  try {
    const cur = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    const next = { ...cur, ...patch };
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    if (_master && typeof next.volume === "number") {
      _master.gain.value = next.volume;
    }
  } catch { /* ignore */ }
}

export function getSoundSettings() {
  return getSettings();
}

function buildReverbImpulse(ctx, durationSec = 0.9, decay = 2.2) {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * durationSec));
  const buf = ctx.createBuffer(2, length, rate);
  for (let ch = 0; ch < 2; ch += 1) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < length; i += 1) {
      const t = 1 - i / length;
      data[i] = (Math.random() * 2 - 1) * Math.pow(t, decay);
    }
  }
  return buf;
}

function ensureCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
    const settings = getSettings();
    _master = _ctx.createGain();
    _master.gain.value = settings.volume;
    _reverb = _ctx.createConvolver();
    _reverb.buffer = buildReverbImpulse(_ctx, 0.7, 2.4);
    const wet = _ctx.createGain();
    wet.gain.value = 0.35;
    _reverb.connect(wet);
    wet.connect(_master);
    _master.connect(_ctx.destination);
  } catch {
    _ctx = null;
  }
  return _ctx;
}

/**
 * Play an elegant tap. Roughly: a low warm sine pulse with a fast pluck-style
 * envelope, plus a tiny noise transient that's reverb-fed for ambience.
 *
 * Optional `pitchHint` (0..1) lightly varies the fundamental so repeated clicks
 * don't sound mechanical (e.g. set it from a card index).
 */
export function playElegantClick(pitchHint = Math.random()) {
  const settings = getSettings();
  if (!settings.enabled) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  const now = ctx.currentTime;
  const baseHz = 320 + 60 * (pitchHint - 0.5); // ~290–350 Hz, dignified midrange

  // --- Body: soft sine pluck ---
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(baseHz * 1.6, now);
  osc.frequency.exponentialRampToValueAtTime(baseHz, now + 0.07);
  oscGain.gain.setValueAtTime(0, now);
  oscGain.gain.linearRampToValueAtTime(0.55, now + 0.005); // quick attack
  oscGain.gain.exponentialRampToValueAtTime(0.0008, now + 0.32); // gentle decay
  osc.connect(oscGain);
  oscGain.connect(_master);
  if (_reverb) oscGain.connect(_reverb);
  osc.start(now);
  osc.stop(now + 0.36);

  // --- Click transient: very short filtered noise burst ---
  const burstLen = 0.025;
  const noiseBuf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * burstLen)), ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i += 1) nd[i] = (Math.random() * 2 - 1) * (1 - i / nd.length);
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 1200;
  filter.Q.value = 0.8;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.18;
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(_master);
  if (_reverb) noiseGain.connect(_reverb);
  noise.start(now);
  noise.stop(now + burstLen);
}

/**
 * Play a short "tick" — softer and shorter than the elegant click. Designed
 * for rapid events (key presses, hover-reveal toggles) so it never feels
 * overwhelming. Quieter and brighter than playElegantClick.
 */
export function playKeyTap(pitchHint = Math.random()) {
  const settings = getSettings();
  if (!settings.enabled) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  const now = ctx.currentTime;
  // Triangle wave around 700–950 Hz → bright but not piercing.
  const baseHz = 800 + 150 * (pitchHint - 0.5);

  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(baseHz * 1.25, now);
  osc.frequency.exponentialRampToValueAtTime(baseHz, now + 0.025);
  oscGain.gain.setValueAtTime(0, now);
  oscGain.gain.linearRampToValueAtTime(0.18, now + 0.003); // very fast attack
  oscGain.gain.exponentialRampToValueAtTime(0.0005, now + 0.09);
  osc.connect(oscGain);
  oscGain.connect(_master);
  // Skip reverb on key taps — keep them tight & dry.
  osc.start(now);
  osc.stop(now + 0.1);
}
